/**
 * User messages may embed completed research reports as `@report:<base64 json>`.
 * Parse for header chips / links; strip from markdown body so raw tokens are not shown.
 */

const REPORT_ATTACHMENT_TOKEN_REGEX = /@report:([A-Za-z0-9+/=]+)/g;

/** Payload shape matches InputBox / backend expectations (`jobId`, `title`, `completedAt`). */
export type ReportAttachmentTokenPayload = {
  jobId: number;
  title: string;
  completedAt: string | null;
  customerId?: number | null;
};

/**
 * Builds the `@report:…` token placed in the composer or sent as part of the user message.
 * Must stay in sync with {@link parseReportAttachmentsFromContent} decoding.
 */
export function encodeReportAttachmentToken(payload: ReportAttachmentTokenPayload): string {
  const body = JSON.stringify({
    jobId: payload.jobId,
    title: payload.title,
    completedAt: payload.completedAt,
    customerId: payload.customerId ?? null,
  });
  const encoded = btoa(unescape(encodeURIComponent(body)));
  return `@report:${encoded}`;
}

export type ParsedReportAttachment = {
  jobId: number;
  title: string;
};

export function parseReportAttachmentsFromContent(rawContent: unknown): ParsedReportAttachment[] {
  const content = String(rawContent || "");
  const matches = Array.from(content.matchAll(REPORT_ATTACHMENT_TOKEN_REGEX));
  const attachments: ParsedReportAttachment[] = [];
  for (const match of matches) {
    try {
      const decoded = atob(String(match[1] || ""));
      const parsed = JSON.parse(decoded);
      const jobId = Number.parseInt(String(parsed?.jobId || ""), 10);
      if (!Number.isFinite(jobId) || jobId < 1) continue;
      const title = String(parsed?.title || `Report ${jobId}`).trim() || `Report ${jobId}`;
      attachments.push({ jobId, title });
    } catch {
      // Ignore malformed tokens in display parser.
    }
  }
  return attachments;
}

export function stripReportAttachmentTokensForDisplay(rawContent: unknown): string {
  const content = String(rawContent || "");
  const stripped = content.replace(REPORT_ATTACHMENT_TOKEN_REGEX, "").replace(/\n{3,}/g, "\n\n");
  return stripped.trim();
}
